import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  Paper, Link,
} from '@mui/material';
import {
  Close as CloseIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import {dealData, getLinkedDeals, transferDealData} from '../deal';

import {
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import {useNotify} from "../../../domains/common/components/AlertNotify";
import {exceptionsTitle} from "../../../dictionaries";


const SyncDrawer = ({
  open,
  onClose,
  currentDealId,
  currentDealTitle
}) => {
  // Состояния
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState(new Set());
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const {openNotify} = useNotify();

  // Функция загрузки данных (запрос на бекенд)
  const fetchLinkedDeals = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getLinkedDeals(dealData.dealId);
      const data = result.ok ? result.data : [];

      const MAX_TITLE_LENGTH = 40; // Максимальная длина заголовка

      const processedDeals = data
        .filter(d => d.dealId !== currentDealId) // Исключаем текущую сделку
        .map(d => ({
          ...d,
          dealTitle: d.title
            ? d.title
            : `Сделка #${d.dealId}`
        }));

      setDeals(processedDeals);
      setSelectedDeals(new Set());
    } catch (err) {
      setError('Ошибка загрузки связанных сделок');
      console.error('Error fetching deals:', err);
    } finally {
      setLoading(false);
    }
  };

  // Загружаем данные при открытии дровера
  useEffect(() => {
    if (open) {
      fetchLinkedDeals();
    }
  }, [open, currentDealId]);

  // Обработчик выбора сделки
  const handleToggleDeal = (dealId) => {
    setSelectedDeals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealId)) {
        newSet.delete(dealId);
      } else {
        newSet.add(dealId);
      }
      return newSet;
    });
  };

  // Обработчик выбора всех сделок
  const handleSelectAll = () => {
    if (selectedDeals.size === deals.length) {
      setSelectedDeals(new Set());
    } else {
      setSelectedDeals(new Set(deals.map(d => d.dealId)));
    }
  };

  // Обработчик синхронизации
  const handleSync = async () => {
    setSyncLoading(true);
    const selectedDealIds = [...selectedDeals];

    const result = await transferDealData(dealData.dealId, selectedDealIds)

    setSyncLoading(false);

    if(result.ok) {
      openNotify({msg: 'Данные успешно перенесены', duration: 3000, type: 'success'})
    } else {
      openNotify({msg: exceptionsTitle.UNKNOWN, duration: 3000, type: 'error'})
    }
  };

  // Обработчик закрытия дровера
  const handleClose = () => {
    if (!syncLoading) {
      onClose();
    }
  };

  // Обработчик закрытия снекбара
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            p: 0
          }
        }}
      >
        {/* Заголовок с кнопкой закрытия */}
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Box>
            <Typography variant="h6" component="h2">
              Перенос данных в связанные сделки
            </Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={syncLoading}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Контент дровера */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {loading ? (
            // Индикатор загрузки
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                gap: 2
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary">
                Загрузка связанных сделок...
              </Typography>
            </Box>
          ) : error ? (
            // Ошибка загрузки
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : deals.length === 0 ? (
            // Пустой список
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                gap: 2
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Нет связанных сделок
              </Typography>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={fetchLinkedDeals}
                disabled={loading}
              >
                Обновить
              </Button>
            </Box>
          ) : (
            <>
              {/* Информация о количестве */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Найдено {deals.length} связанных сделок
                </Typography>
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  disabled={syncLoading}
                >
                  {selectedDeals.size === deals.length
                    ? 'Снять все'
                    : 'Выбрать все'}
                </Button>
              </Box>

              {/* Список сделок */}
              <Paper variant="outlined" sx={{ mb: 3,borderLeft: 'none',
                borderRight: 'none' }}>
                <List disablePadding>
                  {deals.map((deal, index) => {
                    const isSelected = selectedDeals.has(deal.dealId);
                    return (
                      <React.Fragment key={deal.dealId}>
                        <ListItem
                          button
                          onClick={() => handleToggleDeal(deal.dealId)}
                          disabled={syncLoading}
                          sx={{
                            py: 2,
                            bgcolor: isSelected ? 'grey.50' : 'transparent',
                            '&:hover': {
                              bgcolor: isSelected ? 'grey.50' : 'action.hover'
                            }
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleDeal(deal.dealId)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={syncLoading}
                            color="primary"
                          />
                          <ListItemText
                            primary={
                              <Typography variant="body1" fontWeight={400}>
                                {deal.dealTitle}
                              </Typography>
                            }
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Link
                              href={`/deal?id=${deal.dealId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                '&:hover': {
                                  color: 'primary.main'
                                }
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 20 }} />
                            </Link>
                          </Box>
                        </ListItem>
                        {index < deals.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Paper>
            </>
          )}
        </Box>

        {/* Нижняя панель с кнопкой синхронизации */}
        <Box
          sx={{
            p: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Button
            variant="contained"
            startIcon={syncLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={handleSync}
            disabled={selectedDeals.size === 0 || loading || syncLoading}
            fullWidth={false}
            sx={{ minWidth: 200 }}
          >
            {syncLoading ? 'Идёт перенос данных...' : `Перенести данные (${selectedDeals.size})`}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default SyncDrawer;